/* ══════════════════════════════════════════════════════════════
   zerodha.ts — Zerodha Kite Connect API Client
   Login, portfolio holdings, P&L, order placement
   ══════════════════════════════════════════════════════════════ */

export interface ZerodhaConfig {
    apiKey: string;
    apiSecret: string;
    accessToken: string;
    baseUrl: string;
}

export interface ZerodhaHolding {
    symbol: string;
    exchange: string;
    isin: string;
    quantity: number;
    averagePrice: number;
    lastPrice: number;
    pnl: number;
    pnlPercent: number;
    dayChange: number;
    dayChangePercent: number;
    investedValue: number;
    currentValue: number;
}

export interface ZerodhaPosition {
    symbol: string;
    exchange: string;
    product: 'MIS' | 'CNC' | 'NRML';
    quantity: number;
    buyPrice: number;
    sellPrice: number;
    pnl: number;
    multiplier: number;
}

export interface ZerodhaOrder {
    orderId: string;
    symbol: string;
    exchange: string;
    orderType: 'MARKET' | 'LIMIT' | 'SL' | 'SL-M';
    transactionType: 'BUY' | 'SELL';
    quantity: number;
    price: number;
    status: 'COMPLETE' | 'OPEN' | 'CANCELLED' | 'REJECTED';
    orderTimestamp: string;
}

export interface ZerodhaProfile {
    userId: string;
    userName: string;
    email: string;
    broker: 'zerodha';
    exchanges: string[];
}

const DEFAULT_BASE_URL = 'https://api.kite.trade';

function getConfig(): ZerodhaConfig {
    return {
        apiKey: localStorage.getItem('imm-zerodha-api-key') || '',
        apiSecret: localStorage.getItem('imm-zerodha-api-secret') || '',
        accessToken: localStorage.getItem('imm-zerodha-access-token') || '',
        baseUrl: DEFAULT_BASE_URL,
    };
}

async function kiteRequest<T>(endpoint: string, method: 'GET' | 'POST' = 'GET', body?: Record<string, unknown>): Promise<T> {
    const config = getConfig();
    if (!config.accessToken) {
        throw new Error('Zerodha access token not set. Please connect your account.');
    }

    const headers: HeadersInit = {
        'X-Kite-Version': '3',
        'Authorization': `token ${config.apiKey}:${config.accessToken}`,
    };

    const options: RequestInit = { method, headers };
    if (body) {
        (headers as Record<string, string>)['Content-Type'] = 'application/x-www-form-urlencoded';
        options.body = new URLSearchParams(body as Record<string, string>).toString();
    }

    const response = await fetch(`${config.baseUrl}${endpoint}`, options);
    if (!response.ok) {
        throw new Error(`Kite API error: ${response.status} ${response.statusText}`);
    }

    const json = await response.json();
    return json.data;
}

// ─── Auth ───

/** Get login URL for OAuth flow */
export function getLoginUrl(): string {
    const config = getConfig();
    return `https://kite.zerodha.com/connect/login?v=3&api_key=${config.apiKey}`;
}

/** Exchange request token for access token */
export async function generateSession(requestToken: string): Promise<string> {
    const config = getConfig();
    const body = {
        api_key: config.apiKey,
        request_token: requestToken,
        checksum: '', // SHA-256 hash of api_key + request_token + api_secret
    };

    const data = await kiteRequest<{ access_token: string }>('/session/token', 'POST', body);
    localStorage.setItem('imm-zerodha-access-token', data.access_token);
    return data.access_token;
}

// ─── Portfolio ───

/** Get portfolio holdings */
export async function getHoldings(): Promise<ZerodhaHolding[]> {
    try {
        return await kiteRequest<ZerodhaHolding[]>('/portfolio/holdings');
    } catch {
        return [];
    }
}

/** Get open positions */
export async function getPositions(): Promise<{ net: ZerodhaPosition[]; day: ZerodhaPosition[] }> {
    try {
        return await kiteRequest('/portfolio/positions');
    } catch {
        return { net: [], day: [] };
    }
}

/** Get order history */
export async function getOrders(): Promise<ZerodhaOrder[]> {
    try {
        return await kiteRequest<ZerodhaOrder[]>('/orders');
    } catch {
        return [];
    }
}

/** Get user profile */
export async function getProfile(): Promise<ZerodhaProfile> {
    try {
        return await kiteRequest<ZerodhaProfile>('/user/profile');
    } catch {
        return {
            userId: '',
            userName: 'Disconnected',
            email: '',
            broker: 'zerodha',
            exchanges: [],
        };
    }
}

// ─── Connection Status ───

export function isConnected(): boolean {
    return !!localStorage.getItem('imm-zerodha-access-token');
}

export function disconnect(): void {
    localStorage.removeItem('imm-zerodha-access-token');
}
